import { FC, MouseEvent, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";

import { QueryStats, Speed, VerticalShades } from "@mui/icons-material";
import { Box, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { useAsu, RangeType } from "@framework/provider-asu";
import { useSettings } from "@framework/provider-settings";
import { useApiCall } from "@framework/react-hooks";

import { EmptyPlaceholder } from "../../../../../../components/common/empty-placeholder";
import { GaugeChart } from "../../../../../../components/charts/gauge";
import { VerticalBarChart } from "../../../../../../components/charts/vertical";
import { formatDateHumanReadable, SemaforIcon } from "../../../../../../utils";
import { DashboardAccordion } from "../../ui/accordion";
import { OpenableWindow } from "../../ui/openable-window";
import { AnomalyTimeline } from "./anomaly-timeline";
import { ANOMALY_COLORS, ANOMALY_L2_BORDER_COLOR, TciChartView } from "./constants";
import { IAnomalyData, IForecastValues, IShortTermPredictItem } from "./interfaces";
import {
  createEmptyAnomalyData,
  formatShortTermPredictText,
  getShortTermPredictBuildDate,
  getShortTermPredictValidUntilDate,
  normalizeAnomalyData,
} from "./utils";

interface IForecastingContentProps {
  hideOverviewSection?: boolean;
}

export const ForecastingContent: FC<IForecastingContentProps> = ({ hideOverviewSection = false }) => {
  const {
    selectedItem,
    startTimestamp,
    endTimestamp,
    userSelectedRangeType,
    recorderModeInterval,
    userSelectedRecorderModePeriod,
    recorderWindowStart,
  } = useAsu();

  const settings = useSettings();
  const { formatMessage } = useIntl();

  const showOverviewSection = !hideOverviewSection;

  const [tci, setTci] = useState<number>(0);
  const [status, setStatus] = useState<number>(0);
  const [anomalyData, setAnomalyData] = useState<IAnomalyData>(createEmptyAnomalyData);
  const [shortTermPredictItems, setShortTermPredictItems] = useState<IShortTermPredictItem[]>([]);
  const [view, setView] = useState(TciChartView.gauge);

  const { warning, critical } = settings.getTCILevels();
  const trainId = selectedItem && "TrainId" in selectedItem ? selectedItem.TrainId ?? null : null;
  const isRecorderMode = userSelectedRangeType === RangeType.RECORDER_MODE;
  const prevTrainId = useRef(trainId);
  const effectiveStartTimestamp = isRecorderMode ? recorderWindowStart : startTimestamp;
  const timelineStartTimestamp = new Date(endTimestamp);
  timelineStartTimestamp.setDate(timelineStartTimestamp.getDate() - 7);

  const anomalyCounters = [
    {
      key: "count",
      label: formatMessage({ id: "pages.scheme.stateAndForecast.anomaliesCount" }),
      value: anomalyData.count,
      color: "#363846",
      dotColor: "#363846",
    },
    {
      key: "l3",
      value: anomalyData.l3,
      color: ANOMALY_COLORS.l3,
      dotColor: ANOMALY_COLORS.l3,
    },
    {
      key: "l2",
      value: anomalyData.l2,
      color: "#8a6d00",
      dotColor: ANOMALY_COLORS.l2,
    },
    {
      key: "l1",
      value: anomalyData.l1,
      color: ANOMALY_COLORS.l1,
      dotColor: ANOMALY_COLORS.l1,
    },
  ];

  const { fn: getStatus, isLoading: isStatusLoading } = useApiCall(
    async (api, { idTrain, startTimestamp, endTimestamp }: IForecastValues) => {
      try {
        const json = (await api.fetchJson({
          url: `/trains/${idTrain}/status`,
          method: "GET",
          data: {
            startTimestamp,
            endTimestamp,
            useForAlert: true,
          },
        })) as { AlarmStatus: number | null } | null;
        return json?.AlarmStatus ?? 0;
      } catch (error) {
        console.error(error);
        return 0;
      }
    },
    { success: false, error: false },
  );

  const { fn: getTci, isLoading: isTciLoading } = useApiCall(
    async (api, { idTrain, startTimestamp, endTimestamp }: IForecastValues) => {
      try {
        const json = (await api.fetchJson({
          url: `/trains/${idTrain}/tci`,
          method: "GET",
          data: {
            startTimestamp,
            endTimestamp,
          },
        })) as { TCIVal: number | null } | null;
        return json?.TCIVal ?? 0;
      } catch (error) {
        console.error(error);
        return 0;
      }
    },
    { success: false, error: false },
  );

  const { fn: getAnomalyData, isLoading: isAnomalyLoading } = useApiCall(
    async (api, { idTrain, startTimestamp, endTimestamp }: IForecastValues) => {
      try {
        const json = (await api.fetchJson({
          url: `/predictive/anomaly/${idTrain}`,
          method: "GET",
          data: {
            startTimestamp,
            endTimestamp,
          },
        })) as IAnomalyData | null;
        return normalizeAnomalyData(json);
      } catch (error) {
        console.error(error);
        return createEmptyAnomalyData();
      }
    },
    { success: false, error: false },
  );

  const { fn: getShortTermPredict, isLoading: isShortTermPredictLoading } = useApiCall(
    async (api, { idTrain }: { idTrain: number }) => {
      try {
        const json = (await api.fetchJson({
          url: `/predictive/short-term-forecast/${idTrain}`,
          method: "GET",
        })) as { items?: IShortTermPredictItem[] } | null;
        return Array.isArray(json?.items) ? json.items : [];
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    { success: false, error: false },
  );

  const handleChange = (_event: MouseEvent<HTMLElement>, newView: TciChartView | null) => {
    if (newView) {
      setView(newView);
    }
  };

  useEffect(() => {
    setStatus(0);
    setTci(0);
    setAnomalyData(createEmptyAnomalyData());
    setShortTermPredictItems([]);
  }, [trainId, isRecorderMode, recorderModeInterval, userSelectedRecorderModePeriod]);

  useEffect(() => {
    if (!trainId) return;
    if (!effectiveStartTimestamp) return;

    if (isRecorderMode) {
      if (prevTrainId.current !== null && trainId !== prevTrainId.current) {
        prevTrainId.current = trainId;
        return;
      }
    }

    const values = {
      idTrain: trainId,
      startTimestamp: effectiveStartTimestamp,
      endTimestamp,
    };

    const requests = [
      getAnomalyData(undefined, values).then(json =>
        setAnomalyData(typeof json === "object" && json !== null ? json : createEmptyAnomalyData()),
      ),
      getShortTermPredict(undefined, { idTrain: trainId }).then(items =>
        setShortTermPredictItems(Array.isArray(items) ? items : []),
      ),
    ];

    if (showOverviewSection) {
      requests.unshift(
        getStatus(undefined, values).then(AlarmStatus => {
          if (typeof AlarmStatus !== "number") return;
          setStatus(AlarmStatus);
        }),
      );
      requests.unshift(
        getTci(undefined, values).then(TCIVal => {
          if (typeof TCIVal !== "number") return;
          setTci(TCIVal);
        }),
      );
    }

    void Promise.allSettled(requests);
  }, [trainId, endTimestamp, effectiveStartTimestamp, isRecorderMode, showOverviewSection]);

  const loading =
    isAnomalyLoading || isShortTermPredictLoading || (showOverviewSection && (isTciLoading || isStatusLoading));

  if (!trainId) {
    return (
      <Box sx={{ width: "100%", height: "100%", minHeight: 0 }}>
        <EmptyPlaceholder
          icon={<QueryStats />}
          text={formatMessage({ id: "pages.forecasting.stationPlaceholder" })}
          wrapperProps={{ sx: { minHeight: "100%", backgroundColor: "#fafaff" } }}
        />
      </Box>
    );
  }

  return (
    <ProgressOverlay
      isLoading={loading}
      wrapperSx={{
        position: "relative",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          overflowX: "hidden",
          backgroundColor: "#fff",
        }}
      >
        <Stack
          spacing={2}
          alignItems="flex-end"
          justifyContent="center"
          sx={{ pt: 0.5, position: "relative", ml: "auto", mb: 0 }}
        >
          {showOverviewSection ? (
            <ToggleButtonGroup
              size="small"
              value={view}
              onChange={handleChange}
              exclusive
              aria-label="Forecasting view"
              sx={{
                "& .MuiToggleButton-root": {
                  padding: "2px",
                },
              }}
            >
              <ToggleButton value={TciChartView.gauge} key={TciChartView.gauge}>
                <Tooltip
                  title={formatMessage({ id: "components.trainTci.gauge" })}
                  arrow
                  placement="left"
                  disableInteractive
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Speed />
                  </Box>
                </Tooltip>
              </ToggleButton>
              <ToggleButton value={TciChartView.vertical} key={TciChartView.vertical}>
                <Tooltip
                  title={formatMessage({ id: "components.trainTci.vertical" })}
                  arrow
                  placement="left"
                  disableInteractive
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <VerticalShades />
                  </Box>
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          ) : null}
        </Stack>
        {showOverviewSection ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { lg: 15, md: 13 },
                  lineHeight: { lg: "15px", md: "13px" },
                  color: "#363846",
                  mt: 1.5,
                  mb: 0.5,
                }}
              >
                <FormattedMessage id="pages.scheme.stateAndForecast.tci" />
              </Typography>
              {view === TciChartView.gauge ? (
                <GaugeChart value={tci} warningLevel={warning} criticalLevel={critical} />
              ) : (
                <Box sx={{ width: "100%" }}>
                  <VerticalBarChart value={tci} warningLevel={warning} criticalLevel={critical} />
                </Box>
              )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 1 }}>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: { lg: 15, md: 13 },
                  lineHeight: { lg: "15px", md: "13px" },
                  color: "#363846",
                  mr: 1,
                }}
              >
                <FormattedMessage id="pages.scheme.stateAndForecast.status" />:
              </Typography>
              <SemaforIcon status={status} width={20} height={20} />
            </Box>
          </>
        ) : null}
        <Box
          sx={{
            width: "100%",
            mt: showOverviewSection ? 1 : 0,
            px: { xs: 1, md: 2 },
            pb: 1.5,
          }}
        >
          {shortTermPredictItems.length > 0 ? (
            <Box sx={{ mb: 2 }}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { lg: 15, md: 13 },
                  lineHeight: { lg: "20px", md: "18px" },
                  color: "#363846",
                  mb: 1,
                }}
              >
                <FormattedMessage id="pages.scheme.stateAndForecast.shortTermForecastTitle" />
              </Typography>
              <Stack spacing={1.25}>
                {shortTermPredictItems.map(item => {
                  const buildDate = getShortTermPredictBuildDate(item);
                  const validUntilDate = getShortTermPredictValidUntilDate(item);
                  const hasValidDate = Number.isFinite(new Date(buildDate).getTime());
                  const hasValidUntilDate =
                    validUntilDate !== null && Number.isFinite(new Date(validUntilDate).getTime());
                  const asOfLine = hasValidDate
                    ? formatMessage(
                        { id: "pages.scheme.stateAndForecast.shortTermForecastAsOf" },
                        { date: formatDateHumanReadable(buildDate) },
                      )
                    : null;
                  const validUntilLine =
                    hasValidUntilDate && validUntilDate
                      ? formatMessage(
                          { id: "pages.scheme.stateAndForecast.shortTermForecastValidUntil" },
                          { date: formatDateHumanReadable(validUntilDate) },
                        )
                      : null;
                  return (
                    <Box
                      key={`${item.idTrainMechanism}-${buildDate}`}
                      sx={{
                        px: 1,
                        py: 0.75,
                        borderRadius: 1,
                        backgroundColor: "rgba(54, 56, 70, 0.06)",
                      }}
                    >
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#363846", mb: 0.25 }}>
                        {item.mechanismName ||
                          formatMessage({ id: "pages.scheme.stateAndForecast.anomalyTooltip.sourceTypes.mechanism" })}
                      </Typography>
                      {asOfLine ? (
                        <Typography
                          sx={{ fontSize: 11, lineHeight: "14px", color: "#5f6472", mb: validUntilLine ? 0.25 : 0.5 }}
                        >
                          {asOfLine}
                        </Typography>
                      ) : null}
                      {validUntilLine ? (
                        <Typography sx={{ fontSize: 11, lineHeight: "14px", color: "#5f6472", mb: 0.5 }}>
                          {validUntilLine}
                        </Typography>
                      ) : null}
                      <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#363846", whiteSpace: "pre-wrap" }}>
                        {formatShortTermPredictText(item.mainResults)}
                      </Typography>
                      {item.additionalInfo ? (
                        <Typography
                          sx={{ fontSize: 11, lineHeight: "15px", color: "#5f6472", mt: 0.5, whiteSpace: "pre-wrap" }}
                        >
                          {formatShortTermPredictText(item.additionalInfo)}
                        </Typography>
                      ) : null}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ) : null}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { lg: 15, md: 13 },
              lineHeight: { lg: "20px", md: "18px" },
              color: "#363846",
              mb: 1,
            }}
          >
            <FormattedMessage id="pages.scheme.stateAndForecast.anomaliesPeriod" />
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
            {anomalyCounters.map(counter => (
              <Box
                key={counter.key}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: "rgba(54, 56, 70, 0.06)",
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: counter.dotColor,
                    border: counter.dotColor === ANOMALY_COLORS.l2 ? `1px solid ${ANOMALY_L2_BORDER_COLOR}` : "none",
                  }}
                />
                {counter.label ? (
                  <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#5f6472" }}>{counter.label}</Typography>
                ) : null}
                <Typography sx={{ fontSize: 14, lineHeight: "16px", fontWeight: 700, color: counter.color }}>
                  {counter.value}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#5f6472", mb: 0.5 }}>
            <FormattedMessage id="pages.scheme.stateAndForecast.anomaliesHint" />
          </Typography>
          <AnomalyTimeline
            items={anomalyData.items}
            startTimestamp={timelineStartTimestamp}
            endTimestamp={endTimestamp}
          />
        </Box>
      </Box>
    </ProgressOverlay>
  );
};

interface IStateAndForecastProps {
  isNarrow?: boolean;
}

export const StateAndForecast: FC<IStateAndForecastProps> = ({ isNarrow }) => {
  const settings = useSettings();
  const [searchParams] = useSearchParams();
  const { formatMessage } = useIntl();

  const schemeState = settings.getSchemeState();
  const { stateAndForecast } = schemeState;
  const onlyPage = searchParams.get("onlyPage") === "true";
  const currentComponent = searchParams.get("component");
  const title = formatMessage({ id: "pages.scheme.stateAndForecast.title" });

  const setExpanded = (isExpanded: boolean): void => {
    settings.setSchemeState({
      ...schemeState,
      stateAndForecast: isExpanded,
    });
  };

  if (onlyPage && currentComponent === "stateAndForecast") {
    return (
      <OpenableWindow title={title} componentName="stateAndForecast">
        <ForecastingContent />
      </OpenableWindow>
    );
  }

  return (
    <DashboardAccordion
      expanded={stateAndForecast}
      title={title}
      setExpanded={setExpanded}
      componentName="stateAndForecast"
      isNarrow={isNarrow}
    >
      <ForecastingContent />
    </DashboardAccordion>
  );
};
